import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/App";
import { useTrackSection } from "@/hooks/useTrackSection";
import { useExam } from "@/context/ExamContext";
import { Loader2, BarChart3, Target, Flame, ArrowLeft, MapPin } from "lucide-react";

const PrelimsHeatmapPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isSI } = useExam();
  const { toast } = useToast();
  useTrackSection();
  const [loading, setLoading] = useState(false);
  const [siState, setSiState] = useState("TS"); // SI only: TS (Telangana) | AP (Andhra)
  const [result, setResult] = useState(null);

  // SI GS subjects — national GS + state-specific GK (adapts to selected state).
  const siStateName = siState === "TS" ? "Telangana" : "Andhra Pradesh";
  const siExamType = siState === "TS" ? "TGPSC" : "APPSC";
  const siSubjects = [
    "History (National)",
    "Indian Polity & Governance",
    "Indian & World Geography",
    "Indian Economy",
    "Science & Technology",
    "Environment & Ecology",
    "International Relations",
    "Current Affairs (National)",
    `${siStateName} History & Culture`,
    `${siStateName} Geography`,
    `${siStateName} Economy & Policy`,
    `${siStateName} Current Affairs`,
  ];

  // Comprehensive list of all UPSC Prelims subjects (non-overlapping)
  const upscSubjects = [
    // History
    "Ancient India",
    "Medieval India", 
    "Modern India",
    "Indian National Movement",
    "Art & Culture",
    
    // Geography
    "Physical Geography",
    "Indian Geography",
    "World Geography",
    
    // Polity & Governance
    "Polity & Constitution",
    "Governance & Public Policy",
    
    // Economy
    "Indian Economy",
    "Economic Development",
    "Agriculture",
    
    // Environment
    "Environment & Ecology",
    "Biodiversity",
    "Climate Change",
    
    // Science
    "Science & Technology",
    "Space & Defence Technology",
    
    // Security & IR
    "Internal Security",
    "International Relations",
    
    // Miscellaneous
    "Current Affairs",
    "Disaster Management",
    "Social Issues"
  ];

  const subjects = isSI ? siSubjects : upscSubjects;
  const [subject, setSubject] = useState(isSI ? siSubjects[0] : "Polity");

  const handleGenerate = async () => {
    setLoading(true);
    setResult(null);

    try {
      const token = localStorage.getItem("token");
      const examParam = isSI ? `?exam_type=${siExamType}` : "";
      const response = await managedAxios.get(
        `${API_BASE}/prelims/heatmap/${encodeURIComponent(subject)}${examParam}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setResult(response.data);
      toast({
        description: `Generated complete heatmap for ${subject}`
      });
    } catch (err) {
      console.error("Error:", err);
      toast({
        variant: "destructive",
        description: err.response?.data?.detail || "Failed to generate heatmap"
      });
    } finally {
      setLoading(false);
    }
  };

  const getImportanceColor = (importance) => {
    switch (importance) {
      case "Very High":
        return "bg-red-500";
      case "High":
        return "bg-orange-500";
      case "Medium":
        return "bg-yellow-500";
      default:
        return "bg-slate-500";
    }
  };

  const getProbabilityColor = (prob) => {
    if (prob >= 80) return "text-red-600";
    if (prob >= 60) return "text-orange-600";
    if (prob >= 40) return "text-yellow-600";
    return "text-slate-600";
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
      {/* Back Button */}
      <Button variant="outline" onClick={() => navigate('/prelims')} className="mb-4">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Prelims Hub
      </Button>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Topic Heatmap</h1>
          <p className="text-sm text-slate-600">
            {isSI ? "GS importance matrix — Telangana & Andhra Pradesh" : "Importance matrix for all UPSC topics"}
          </p>
        </div>
        <Badge className="bg-green-500">
          🔓 FREE
        </Badge>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <BarChart3 className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-blue-900 mb-1">
                Complete Heatmap Analysis:
              </p>
              <p className="text-sm text-slate-700">
                View all topics with importance levels, probability scores, 
                PYQ frequency, and Current Affairs relevance for strategic preparation.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Configuration Card */}
      <Card>
        <CardContent className="p-6 space-y-4">
          {isSI && (
            <div>
              <Label className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-indigo-600" /> Select State</Label>
              <div className="grid grid-cols-2 gap-2 mt-1.5">
                {[
                  { id: "TS", label: "Telangana (TS)" },
                  { id: "AP", label: "Andhra Pradesh (AP)" },
                ].map((st) => (
                  <button
                    key={st.id}
                    type="button"
                    disabled={loading}
                    onClick={() => { setSiState(st.id); setSubject(st.id === "TS" ? "History (National)" : "History (National)"); setResult(null); }}
                    className={`px-3 py-2.5 rounded-xl border-2 text-sm font-semibold transition-all ${
                      siState === st.id ? "border-indigo-500 bg-indigo-50 text-indigo-700" : "border-slate-200 text-slate-600 hover:border-indigo-300"
                    }`}
                    data-testid={`heatmap-state-${st.id}`}
                  >
                    {st.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div>
            <Label htmlFor="subject">Select {isSI ? "GS Subject" : "Subject"}</Label>
            <select
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={loading}
              className="w-full px-3 py-2 border border-slate-300 rounded-md"
            >
              {subjects.map((sub) => (
                <option key={sub} value={sub}>
                  {sub}
                </option>
              ))}
            </select>
          </div>

          <Button
            onClick={handleGenerate}
            disabled={loading}
            className="w-full bg-gradient-to-r from-green-500 to-emerald-600"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Generating Heatmap...
              </>
            ) : (
              <>
                <BarChart3 className="w-4 h-4 mr-2" />
                Generate Heatmap
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Results */}
      {result && (
        <div className="space-y-4">
          {/* Top Topic Highlight */}
          {result.top_topic && (
            <Card className="bg-gradient-to-br from-red-50 to-orange-50 border-red-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <Flame className="w-6 h-6 text-red-600" />
                  <h3 className="font-bold text-slate-900 text-lg">
                    #1 Most Important Topic
                  </h3>
                </div>
                <div className="space-y-3">
                  <p className="text-xl font-bold text-slate-900">
                    {result.top_topic.topic}
                  </p>
                  <div className="flex items-center gap-4">
                    <Badge className={getImportanceColor(result.top_topic.importance)}>
                      {result.top_topic.importance}
                    </Badge>
                    <div>
                      <p className="text-sm text-slate-600">Probability</p>
                      <p className={`text-2xl font-bold ${getProbabilityColor(result.top_topic.probability)}`}>
                        {result.top_topic.probability}%
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Complete Heatmap */}
          {result.heatmap && result.heatmap.length > 0 && (
            <>
              <div className="flex items-center gap-2">
                <Target className="w-5 h-5 text-green-600" />
                <h3 className="font-semibold text-slate-900">
                  Complete Topic Heatmap for {subject}
                </h3>
              </div>

              {/* Legend */}
              <Card className="bg-slate-50">
                <CardContent className="p-4">
                  <div className="flex flex-wrap items-center gap-4 text-xs">
                    <span className="font-semibold text-slate-700">Importance:</span>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded bg-red-500"></div>
                      <span className="text-slate-600">Very High</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded bg-orange-500"></div>
                      <span className="text-slate-600">High</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded bg-yellow-500"></div>
                      <span className="text-slate-600">Medium</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded bg-slate-500"></div>
                      <span className="text-slate-600">Low</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Heatmap Table */}
              <div className="space-y-2">
                {result.heatmap.map((item, idx) => (
                  <Card
                    key={idx}
                    className={`border-l-4 ${
                      item.importance === "Very High"
                        ? "border-l-red-500 bg-red-50"
                        : item.importance === "High"
                        ? "border-l-orange-500 bg-orange-50"
                        : item.importance === "Medium"
                        ? "border-l-yellow-500 bg-yellow-50"
                        : "border-l-slate-500 bg-slate-50"
                    }`}
                  >
                    <CardContent className="p-4">
                      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
                        {/* Rank */}
                        <div className="md:col-span-1">
                          <Badge variant="outline" className="text-lg font-bold">
                            #{idx + 1}
                          </Badge>
                        </div>

                        {/* Topic */}
                        <div className="md:col-span-5">
                          <p className="font-semibold text-slate-900">{item.topic}</p>
                        </div>

                        {/* Importance */}
                        <div className="md:col-span-2">
                          <p className="text-xs text-slate-600 mb-1">Importance</p>
                          <Badge className={getImportanceColor(item.importance)}>
                            {item.importance}
                          </Badge>
                        </div>

                        {/* Probability */}
                        <div className="md:col-span-2">
                          <p className="text-xs text-slate-600 mb-1">Probability</p>
                          <p className={`text-xl font-bold ${getProbabilityColor(item.probability)}`}>
                            {item.probability}%
                          </p>
                        </div>

                        {/* Additional Metrics */}
                        <div className="md:col-span-2">
                          <div className="space-y-1">
                            {item.pyq_frequency !== undefined && (
                              <p className="text-xs text-slate-600">
                                PYQ: {item.pyq_frequency}
                              </p>
                            )}
                            {item.ca_relevance && (
                              <Badge variant="secondary" className="text-xs">
                                CA: {item.ca_relevance}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          )}

          {/* Tips Card */}
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold text-amber-900 mb-2">
                💡 How to Use This Heatmap
              </h3>
              <ul className="text-sm text-slate-700 space-y-1">
                <li>• Focus on <strong>Very High</strong> and <strong>High</strong> importance topics first</li>
                <li>• Topics with high probability (80%+) are likely to appear in upcoming exams</li>
                <li>• Check PYQ frequency to understand historical patterns</li>
                <li>• CA relevance indicates current affairs connection</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default PrelimsHeatmapPage;
